<template>
    <div>
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                v-if="model"
                ref="RkmForm"
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo.end"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo.start"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field='id'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :options="form.receitaTipos"
                            @change="handleReceitaTipoChange"
                            default-option-text="Todos"
                            label="Tipo de receita"
                            show-default-option
                            text-field="valor"
                            v-model="model.receitaTipo"
                            value-field='valor'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :repository="repositories.relatorioAtendimentoDiario.paciente"
                            :request-query-string-params="requestQueryStringParamsPaciente"
                            :search-fields="searchFieldsPaciente"
                            label="Paciente"
                            text-field="nome"
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-button
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :label="false"
                            @click="handleBuscarClick"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :label="false"
                            @click="handleLimparClick"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="handleTodosClick"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="handleImprimirClick"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                class="table-resultado"
            >
                <template v-slot:afterTable>
                    <div class="table-resultado-header-footer text-center">
                        <span>
                            <strong>Total de pacientes:</strong> {{ getSafely(() => totalizadores.pacientes, 0) }}
                        </span>

                        <span class="ml-4">
                            <strong>Total de medicamentos:</strong> {{ getSafely(() => totalizadores.medicamentos, 0) }}
                        </span>
                    </div>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { error, object, repository, stringCase, formats } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        fields() {
            return this.columns.filter(column => (column.fixed || false) || this.selectedColumns.includes(column.key))
        },
        optionalColumns() {
            return this.columns.filter(column => !(column.fixed || false))
        },
        validations() {
            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                }
            }
        },
        searchFieldsPaciente() {
            return [
                { key: 'cadastro_paciente.prontuario', searchable: true },
                { key: 'cadastro_cidadao.nome', searchable: true },
            ]
        },
        requestQueryStringParamsPaciente() {
            const startDate = this.others.moment(this.model.periodo.start).format('YYYY-MM-DD')
            const endDate = this.others.moment(this.model.periodo.end).format('YYYY-MM-DD')

            const request = {
                startDate,
                endDate,
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                receitaTipo: this.model.receitaTipo,
            }

            return this.camelObjectKeysToSnakeRecursive(request)
        },
    },
    created() {
        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.farmacia'),
            relatorioAtendimentoDiario: this.getConectaSusRepository('farmacia.relatorioAtendimentoDiario'),
        }
    },
    data() {
        return {
            additionals: null,
            columns: [
                {
                    key: 'dataDispensacao',
                    label: 'Data de dispensação',
                    class: 'col-min text-center',
                    sortable: true,
                    formatter: value => this.formatDate(value),
                    filterByFormatted: true,
                },
                { key: 'unidadeNomeFantasia', label: 'Unidade', fixed: true, sortable: true },
                { key: 'infraestruturaNome', label: 'Local de atendimento', sortable: true },
                { key: 'pacienteProntuario', label: 'Prontuário', fixed: true, sortable: true },
                { key: 'pacienteNome', label: 'Paciente', fixed: true, sortable: true },
                { key: 'receitaTipo', label: 'Tipo de receita', class: 'text-center', fixed: true, sortable: true },
                { key: 'quantidade', label: 'Quantidade', sortable: true },
            ],
            form: null,
            indeterminate: true,
            itens: [],
            model: null,
            others: { moment },
            selectedAll: false,
            selectedColumns: [
                'dataDispensacao',
                'unidadeNomeFantasia',
                'infraestruturaNome',
                'pacienteProntuario',
                'pacienteNome',
                'receitaTipo',
            ],
            totalizadores: null,
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioAtendimentoDiario.create()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange(value) {
            this.form.infraestruturas = []
            this.form.receitaTipos = []
            this.model.infraestruturaId = null
            this.model.receitaTipo = null

            this.model.paciente = {
                id: null,
                nome: null,
            }

            this.itens = []

            if (value) {
                const startDate = this.others.moment(this.model.periodo.start).format('YYYY-MM-DD')
                const endDate = this.others.moment(this.model.periodo.end).format('YYYY-MM-DD')

                this.getInfraestruturas(startDate, endDate, value)
            }
        },
        handleInfraestruturaChange(value) {
            this.form.receitaTipos = []
            this.model.receitaTipo = null

            this.model.paciente = {
                id: null,
                nome: null,
            }

            this.itens = []

            if (value) {
                const startDate = this.others.moment(this.model.periodo.start).format('YYYY-MM-DD')
                const endDate = this.others.moment(this.model.periodo.end).format('YYYY-MM-DD')

                this.getReceitaTipos(startDate, endDate, value)
            }
        },
        handleReceitaTipoChange() {
            this.model.paciente = {
                id: null,
                nome: null,
            }

            this.itens = []
        },
        handleBuscarClick() {
            this.setLoading()

            const request = {
                startDate: this.others.moment(this.model.periodo.start).format('YYYY-MM-DD'),
                endDate: this.others.moment(this.model.periodo.end).format('YYYY-MM-DD'),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                receitaTipo: this.model.receitaTipo,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
            }

            this.repositories.relatorioAtendimentoDiario.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.itens = Object.assign(response.data.dispensacoes)
                    this.totalizadores = response.data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleLimparClick() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.receitaTipos = []

            this.model.periodo = {
                start: null,
                end: null,
            }

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.receitaTipo = null

            this.model.paciente = {
                id: null,
                nome: null,
            }

            this.itens = []
        },
        handleTodosClick(selected) {
            this.selectedColumns = selected ? this.optionalColumns.map(c => c.key) : []
        },
        handleImprimirClick() {
            this.setLoading()

            const request = {
                startDate: this.others.moment(this.model.periodo.start).format('YYYY-MM-DD'),
                endDate: this.others.moment(this.model.periodo.end).format('YYYY-MM-DD'),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                receitaTipo: this.model.receitaTipo,
                pacienteId: this.model.paciente ? this.model.paciente.id : null,
                selectedColumns: this.fields.map(field => ({
                    class: field.class,
                    key: this.camelToSnake(field.key),
                    label: field.label,
                })),
            }

            this.repositories.impressao.relatorioAtendimentoDiario(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getUnidades(startDate, endDate) {
            this.setLoading()

            const request = {
                listar: 'unidades',
                startDate,
                endDate,
            }

            this.repositories.relatorioAtendimentoDiario.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.unidades = Object.assign(response.data)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getInfraestruturas(startDate, endDate, unidadeId) {
            this.setLoading()

            const request = {
                listar: 'infraestruturas',
                startDate,
                endDate,
                unidadeId,
            }

            this.repositories.relatorioAtendimentoDiario.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getReceitaTipos(startDate, endDate, infraestruturaId) {
            this.setLoading()

            const request = {
                listar: 'receita-tipos',
                startDate,
                endDate,
                infraestruturaId,
            }

            this.repositories.relatorioAtendimentoDiario.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.receitaTipos = Object.assign(response.data)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mounted() {
        this.load()
    },
    mixins: [ error, object, repository, stringCase, formats ],
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                if (value.start && value.end) {
                    const startDate = this.others.moment(value.start).format('YYYY-MM-DD')
                    const endDate = this.others.moment(value.end).format('YYYY-MM-DD')

                    this.getUnidades(startDate, endDate)
                }
            },
        },
        selectedColumns(value) {
            this.indeterminate = value.length > 0 && value.length != this.optionalColumns.length
            this.selectedAll = value.length > 0 && value.length == this.optionalColumns.length
        },
    },
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 300px;
}
.table-resultado >>> .table-resultado-header-footer {
    background: #f1f1f1;
    border: 1px solid #dee2e6;
    color: var(--gray-dark);
    font-weight: 600;
    padding: 0.5rem;
}
</style>